import { onLogout } from '../constants/global';
import { pageurl } from '../constants/pageurl';

/**
 * Ending an Independent Supporter's session when api.core stops honouring it mid-session.
 *
 * <p>A Super Admin can suspend or close a supporter at any time, and api.core's account gate then
 * refuses the supporter's token within about 30 seconds (a bare 401, no body). Leaving them on a
 * home whose every read fails says nothing useful, so the session ends: the cookie goes, and the
 * sign-in screen says why. The reason survives the page load in sessionStorage — not localStorage,
 * which `onLogout` clears.</p>
 *
 * <p>The 401 carries no wording, so the default copy says what is true without guessing which of
 * suspended, closed or expired it was. Signing in again gets api.core's own words for it:
 * `loginwithotp` refuses a suspended or closed supporter with a 403 naming the reason.</p>
 */
const REASON_KEY = 'ielect-session-ended-reason';

export const SUPPORTER_SESSION_ENDED =
	'Your supporter session has ended. Sign in again — if your access has changed, you will be told why.';

export const endSupporterSession = (message?: string | null) => {
	try {
		sessionStorage.setItem(REASON_KEY, message?.trim() || SUPPORTER_SESSION_ENDED);
	} catch {
		// Storage unavailable — the sign-in screen simply shows no reason.
	}
	onLogout();
	window.open(pageurl.LOGIN, '_self');
};

/** The reason the last session was ended, or null. Pure read: pair it with `clearSessionEndedReason`. */
export const readSessionEndedReason = (): string | null => {
	try {
		return sessionStorage.getItem(REASON_KEY);
	} catch {
		return null;
	}
};

/** Said once: a later visit to the sign-in screen must not repeat it. */
export const clearSessionEndedReason = () => {
	try {
		sessionStorage.removeItem(REASON_KEY);
	} catch {
		// Nothing to clear.
	}
};
